import {
  Button,
  FormControl,
  MenuItem,
  Select,
  Typography,
  TextField,
  MenuProps,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'
import { deepPurple } from '@material-ui/core/colors'
import ArrowDropDownRoundedIcon from '@material-ui/icons/ArrowDropDownRounded'
import Image from 'next/image'

import { Cryptocurrency } from '../../../../../types/currency'
import { useFiltersContext } from '../../../../contexts/Filters'
import { Filters } from '../../../../../types/general'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    width: '100%',
    flexWrap: 'wrap',
    margin: '20px 0 50px 0',
    [theme.breakpoints.down('xs')]: {
      marginTop: '20px',
      padding: '10px',
    },
  },
  filterLabel: {
    marginBottom: '10px',
    fontSize: '14px',
    letterSpacing: '0',
  },
  currencyIcon: {
    marginRight: '10px',
  },
  purpleText: {
    color: theme.palette.primary.main,
    fontSize: '16px',
    margin: '0 0 0 auto',
    '& input': {
      fontWeight: 'bold',
      padding: '.6rem 1rem ',
    },
    '& fieldset': {
      borderRadius: '1rem',
      border: '1px solid rgba(0,0,0,0.3)',
    },
  },
  selected: {
    borderColor: `${theme.palette.primary.main} !important`,
  },
  menuItem: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '28px',
    '&:hover': {
      opacity: 0.8,
      transition: 'opacity .2s ease-out',
      '-moz-transition': 'opacity .2s ease-out',
      '-webkit-transition': 'opacity .2s ease-out',
      '-o-transition': 'opacity .2s ease-out',
    },
  },
  formControl: {
    height: '40px',
    '& fieldset': {
      borderRadius: '1rem',
      border: '1px solid rgba(0,0,0,0.3)',
    },
    '& .MuiInputBase-root': {
      height: '58px',
    },
    [theme.breakpoints.down('xs')]: {
      margin: '0',
    },
  },

  input: {
    color: theme.palette.secondary.main,
    fontWeight: 600,
  },

  button: {
    height: '40px',
    width: '250px',
    margin: '0 0 0 auto',
    '& h6': {
      fontSize: '1rem',
      textTransform: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      margin: '10px 0',
    },
  },
  inputContainer: {
    margin: '0 10px',
    [theme.breakpoints.down('xs')]: {
      margin: '10px 0',
      flexBasis: '100%',
    },
  },
  select: {
    width: '100%',
    background: 'white',
    color: deepPurple[500],
    fontWeight: 600,
    borderWidth: 2,
    borderRadius: 12,
    padding: '.4rem .8rem',
    fontSize: '1rem',
    minWidth: '7rem',
    '&:focus': {
      background: 'white',
      borderColor: deepPurple[100],
    },
  },
  paper: {
    borderRadius: 12,
    marginTop: 8,
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
    background: 'white',
    fontSize: '1rem',
    '& li': {
      fontWeight: 200,
      paddingTop: 12,
      paddingBottom: 12,
      fontSize: '.8rem',
    },
    '& li:hover': {
      background: deepPurple[100],
    },
    '& li.Mui-selected': {
      color: 'white',
      background: deepPurple[400],
      fontWeight: 600,
    },
    '& li.Mui-selected:hover': {
      background: deepPurple[500],
    },
  },
  valueContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    marginRight: '.2rem',
  },
  valueImageContainer: {
    height: '1.2rem',
    width: '1rem',
    display: 'inline-block',
    position: 'relative',
    verticalAlign: 'middle',
    overflow: 'hidden',
    marginRight: '.8rem',
  },
  value: {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    color: 'black',
    textTransform: 'capitalize',
  },
  inputOutline: {
    borderRadius: '1rem',
  },
}))

interface Props {
  depositCryptocurrencies: Cryptocurrency[]
  onFilter: (filters: Filters) => Promise<void>
}

export default function SavingsAccountFilters({
  depositCryptocurrencies,
  onFilter,
}: Props): JSX.Element {
  const classes = useStyles({})
  const selectProps = {
    classes: {
      paper: classes.paper,
      list: classes.list,
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
  } as MenuProps

  const context = useFiltersContext()
  const selectedDepositCurrency =
    context.filters?.depositCryptocurrency || depositCryptocurrencies[0]
  const [depositInput, setDepositInput] = useState<number>(1000)
  const [depositPeriod, setDepositPeriod] = useState<'1' | '2' | '3'>('1')
  const selectDeposit = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selected = depositCryptocurrencies.find((obj) => obj.code === event.target.value)
    context.setFilter({
      depositCryptocurrency: selected,
    })
  }

  function compareRates() {
    context.setFilter({
      depositPeriod,
      depositInput,
    })
  }

  function onInputChange(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    setDepositInput(parseFloat(event.target.value))
  }

  function onPeriodChange(
    event: React.ChangeEvent<{
      name?: string | undefined
      value: unknown
    }>,
  ) {
    setDepositPeriod(event.target.value as typeof depositPeriod)
  }

  useEffect(() => {
    if (onFilter) {
      onFilter({
        depositCryptocurrency: selectedDepositCurrency,
      })
    }
  }, [selectedDepositCurrency])

  return (
    <div className={classes.container}>
      <div className={classes.inputContainer}>
        <Typography variant="subtitle1" className={classes.filterLabel}>
          $ Amount
        </Typography>
        <TextField
          variant="outlined"
          value={depositInput}
          onChange={onInputChange}
          classes={{ root: classes.purpleText }}
          size="small"
        />
      </div>
      <div className={classes.inputContainer}>
        <Typography variant="subtitle1" className={classes.filterLabel}>
          Deposit
        </Typography>
        <FormControl variant="outlined" className={classes.formControl}>
          <Select
            onChange={selectDeposit}
            value={selectedDepositCurrency?.code}
            classes={{ root: classes.select }}
            MenuProps={selectProps}
            IconComponent={ArrowDropDownRoundedIcon}
            renderValue={() => (
              <span className={classes.valueContainer}>
                {selectedDepositCurrency.icon_primary?.url && (
                  <span className={classes.valueImageContainer}>
                    <Image
                      src={selectedDepositCurrency.icon_primary.url}
                      layout="fill"
                      objectFit="contain"
                    />
                  </span>
                )}
                <Typography variant="body2" className={classes.value}>
                  {selectedDepositCurrency?.name}
                </Typography>
              </span>
            )}
          >
            {depositCryptocurrencies.map((currency) => (
              <MenuItem value={currency.code} key={currency.code}>
                <span className={classes.menuItem}>
                  <img
                    width={15}
                    height={20}
                    src={currency.icon_primary?.url}
                    alt={`${currency.name} icon`}
                    className={classes.currencyIcon}
                  />
                  <Typography variant="subtitle1" className={classes.purpleText}>
                    {currency.name}
                  </Typography>
                </span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className={classes.inputContainer}>
        <Typography variant="subtitle1" className={classes.filterLabel}>
          Deposit period
        </Typography>
        <FormControl variant="outlined" className={classes.formControl}>
          <Select
            disableUnderline
            onChange={onPeriodChange}
            classes={{ root: classes.select }}
            value={depositPeriod}
            renderValue={() => (
              <span className={classes.valueContainer}>
                <Typography variant="body2" className={classes.value}>
                  {depositPeriod} year
                </Typography>
              </span>
            )}
            MenuProps={selectProps}
          >
            <MenuItem value="1" key="1">
              <Typography variant="subtitle1" className={classes.purpleText}>
                1 year
              </Typography>
            </MenuItem>
            <MenuItem value="2" key="2">
              <Typography variant="subtitle1" className={classes.purpleText}>
                2 year
              </Typography>
            </MenuItem>
            <MenuItem value="3" key="3">
              <Typography variant="subtitle1" className={classes.purpleText}>
                3 year
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>
      </div>
      <Button variant="contained" className={classes.button} color="primary" onClick={compareRates}>
        <Typography variant="h6">Compare rates</Typography>
      </Button>
    </div>
  )
}
